import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { MapContainer, TileLayer, CircleMarker, Marker, Polyline, Tooltip as LeafletTooltip, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
// Side-effect import: attaches L.heatLayer (typed by @types/leaflet.heat). Must come after
// `import L from 'leaflet'` — the plugin's dist bundle expects the Leaflet global to exist.
import 'leaflet.heat';
import { PageHeader } from '../ui/PageHeader';
import { ErrorBanner } from '../ui/ErrorBanner';
import { Skeleton, SkeletonCards } from '../ui/Skeleton';
import { StatCard } from '../ui/StatCard';
import { supabase } from '../lib/supabase';
import { fetchAllRows } from '../lib/fetchAll';

/**
 * Schema:
 * - cities: id, name ("New Orleans, LA, USA"), lat, lng — 267 featured metros, all with coords.
 * - profiles: id, city — free-text home city ("New Orleans, LA, USA" or just "Rogers, AR"),
 *   set by the user; NOT a foreign key. Matched to `cities` here by lowercasing + trimming the
 *   FIRST comma-segment on both sides (same convention as the `admin_home_city_user_counts` RPC
 *   used on the Overview screen — done client-side here so unmatched cities are surfaced instead
 *   of silently dropped).
 * - venues: id, venue_name_user_input, latitude, longitude, venue_shado_rating_count.
 *   RLS `venues_select_authenticated` allows any signed-in dashboard session (admin or venue
 *   reviewer) to read non-hidden venues directly — no RPC needed for this screen.
 */

type CityCoordRow = { id: string; name: string | null; lat: number | null; lng: number | null };
type ProfileCityRow = { id: string; city: string | null };
type RatedVenueRow = {
  id: string;
  venue_name_user_input: string | null;
  latitude: number | null;
  longitude: number | null;
  venue_shado_rating_count: number | null;
};

type LayerKey = 'users' | 'venues' | 'density';

const LAYER_OPTIONS: { key: LayerKey; label: string }[] = [
  { key: 'users', label: 'Users' },
  { key: 'venues', label: 'Rated venues' },
  { key: 'density', label: 'Rating density' },
];

type DisplayMode = 'circles' | 'heat';

type LabelMetric = 'users' | 'venues' | 'ratings' | 'both';
type LabelStyle = 'pill' | 'glass' | 'numeral' | 'stacked';
type LabelSize = 'sm' | 'md' | 'lg';

const LABEL_METRIC_OPTIONS: { key: LabelMetric; label: string }[] = [
  { key: 'users', label: 'Users' },
  { key: 'venues', label: 'Venues rated' },
  { key: 'ratings', label: 'Ratings' },
  { key: 'both', label: 'Users + venues' },
];

const LABEL_STYLE_OPTIONS: { key: LabelStyle; label: string }[] = [
  { key: 'pill', label: 'Pill' },
  { key: 'glass', label: 'Glass' },
  { key: 'numeral', label: 'Numeral' },
  { key: 'stacked', label: 'Stacked' },
];

const LABEL_SIZE_SCALE: Record<LabelSize, number> = { sm: 0.85, md: 1, lg: 1.28 };

/**
 * A rated venue is credited to the nearest featured city within this radius. Metros sprawl —
 * Deep Ellum is 4km from downtown Dallas, Brooklyn 12km from Manhattan — and anything beyond
 * ~60km is a different market, so counting it toward a city would overstate that city's coverage.
 */
const VENUE_CITY_RADIUS_KM = 60;

const EARTH_RADIUS_KM = 6371;

function haversineKm(aLat: number, aLng: number, bLat: number, bLng: number): number {
  const toRad = Math.PI / 180;
  const dLat = (bLat - aLat) * toRad;
  const dLng = (bLng - aLng) * toRad;
  const h =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(aLat * toRad) * Math.cos(bLat * toRad) * Math.sin(dLng / 2) ** 2;
  return 2 * EARTH_RADIUS_KM * Math.asin(Math.min(1, Math.sqrt(h)));
}

type CityStat = {
  id: string;
  label: string;
  lat: number;
  lng: number;
  users: number;
  venues: number;
  ratings: number;
};

const DISPLAY_OPTIONS: { key: DisplayMode; label: string }[] = [
  { key: 'circles', label: 'Circles' },
  { key: 'heat', label: 'Heat' },
];

/**
 * Brand gradient tuned for the dark CARTO tiles: deep violet low → accent mid →
 * light lavender high → warm gold peak. Intensities below the first stop clamp to
 * the deep violet and fade out through the heat canvas's alpha, so cold zones stay subtle.
 */
/** 2026-08-10 (Evan): "change heat color back to purple" — the gold peak is gone. A single hue
 *  ramping deep violet → accent → lavender reads as one quantity getting more intense; the gold
 *  stop read as a second, different thing happening at the top end. */
const HEAT_GRADIENT: L.ColorGradientConfig = {
  0.1: '#3B117F',
  0.35: '#7C3AED',
  0.68: '#A78BFA',
  1: '#DDD0FF',
};

type CityUserPoint = { id: string; label: string; lat: number; lng: number; count: number };
type UnmatchedCityAgg = { key: string; label: string; count: number };
type VenuePoint = { id: string; label: string; lat: number; lng: number; count: number };
type TopRow = { label: string; count: number };

function firstSegment(raw: string): string {
  const seg = raw.split(',')[0] ?? raw;
  return seg.trim();
}

function cityKey(raw: string): string {
  return firstSegment(raw).toLowerCase();
}

/** Radius grows with sqrt(count) so area (not radius) tracks the count roughly linearly. */
function userMarkerRadius(count: number): number {
  return Math.min(46, 5 + 2.6 * Math.sqrt(count));
}

function densityMarkerRadius(count: number): number {
  return Math.min(34, 4 + 1.9 * Math.sqrt(count));
}

const VENUE_DOT_RADIUS = 4.5;

function pluralize(n: number, noun: string): string {
  return `${n.toLocaleString()} ${noun}${n === 1 ? '' : 's'}`;
}

/** Fits the map to the active layer's points on load / layer switch; world view when empty. */
/**
 * ⚠️ FITS ONCE, THEN GETS OUT OF THE WAY. It used to re-fit whenever the point set changed — which
 * includes every Users/Venues/Density toggle — so switching layer silently threw away wherever you
 * had navigated to. That is the "can't zoom in and out" complaint: you could zoom fine, you just
 * kept losing it.
 *
 * Now it fits on the FIRST dataset only, and any manual move sets a flag that stops it ever fitting
 * again. Re-framing is on an explicit button instead, so it happens when you ask rather than as a
 * side effect of changing what you are looking at.
 *
 * `maxZoom: 12` also capped the initial fit well short of street level; a single-city dataset should
 * be allowed to land close.
 */
function FitToPoints({ points, fitNonce }: { points: [number, number][]; fitNonce: number }) {
  const map = useMap();
  const userMoved = useRef(false);
  const didFit = useRef(false);

  useEffect(() => {
    const onUserMove = () => { userMoved.current = true; };
    map.on('dragstart', onUserMove);
    map.on('zoomstart', onUserMove);
    return () => {
      map.off('dragstart', onUserMove);
      map.off('zoomstart', onUserMove);
    };
  }, [map]);

  useEffect(() => {
    /** `fitNonce` is bumped by the Fit button — an explicit request always wins. */
    const forced = fitNonce > 0;
    if (!forced && (didFit.current || userMoved.current)) return;
    if (points.length === 0) { map.setView([20, 0], 2); return; }
    if (points.length === 1) { map.setView(points[0], 13); didFit.current = true; return; }
    const bounds = L.latLngBounds(points.map(([lat, lng]) => L.latLng(lat, lng)));
    map.fitBounds(bounds, { padding: [32, 32], maxZoom: 14 });
    didFit.current = true;
    if (forced) userMoved.current = false;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, points, fitNonce]);
  return null;
}

/** Live zoom readout + a re-frame control — navigation you can see rather than guess at. */
function MapHud({ onFit }: { onFit: () => void }) {
  const map = useMap();
  const [z, setZ] = useState(map.getZoom());
  useEffect(() => {
    const h = () => setZ(map.getZoom());
    map.on('zoom', h);
    return () => { map.off('zoom', h); };
  }, [map]);
  return (
    <div className="pointer-events-auto absolute right-2 top-2 z-[500] flex items-center gap-1.5 rounded-lg border border-border bg-surface/90 px-2 py-1 backdrop-blur">
      <span className="tabular-nums text-[11px] text-mutedDim">z{z.toFixed(1)}</span>
      <button
        type="button"
        onClick={() => map.setZoom(map.getZoom() - 1)}
        className="rounded px-1.5 py-0.5 text-[13px] leading-none text-textPrimary hover:bg-white/10"
        aria-label="Zoom out"
      >−</button>
      <button
        type="button"
        onClick={() => map.setZoom(map.getZoom() + 1)}
        className="rounded px-1.5 py-0.5 text-[13px] leading-none text-textPrimary hover:bg-white/10"
        aria-label="Zoom in"
      >+</button>
      <button
        type="button"
        onClick={onFit}
        className="rounded px-1.5 py-0.5 text-[11px] leading-none text-textPrimary hover:bg-white/10"
      >Fit</button>
    </div>
  );
}

/**
 * react-leaflet bridge for the leaflet.heat plugin (which predates react-leaflet).
 *
 * ⚠️ THE RADIUS IS GEOGRAPHIC, NOT A FIXED NUMBER OF SCREEN PIXELS — and that single fact is what
 * was wrong with this map (Evan 2026-08-10: "being zoomed out / in i dont feel like the heat map is
 * very representative").
 *
 * leaflet.heat takes `radius` in pixels and never changes it, so one blob covered a constant patch
 * of SCREEN while the ground under it changed by orders of magnitude. Zoomed out to the continent,
 * a 32px blob spans ~600km: every US city fused into one smear and three users looked exactly like
 * three hundred. Zoomed into a metro, that same 32px covers a few blocks, so the city that was a
 * blazing hotspot a moment ago shrivelled to a dot on an empty map. The heat was never wrong about
 * the data — it was measuring the screen instead of the world.
 *
 * Fixing it means the blob owning a fixed patch of GROUND: convert `radiusKm` to pixels at the
 * current zoom and latitude, and recompute on every zoom. Then a city's footprint means the same
 * thing at z3 and z13, which is the entire point of a density surface. Clamped so it can neither
 * vanish nor swallow the viewport, and `maxZoom` is pinned to the live zoom so the plugin stops
 * applying its own second intensity ramp on top of the one we already control through `max`.
 */
function HeatmapLayer({
  points, max, spread, brightness,
}: {
  points: L.HeatLatLngTuple[];
  max: number;
  spread: number;
  brightness: number;
}) {
  const map = useMap();
  const [zoom, setZoom] = useState(() => map.getZoom());

  useEffect(() => {
    const h = () => setZoom(map.getZoom());
    map.on('zoom', h);
    map.on('zoomend', h);
    return () => { map.off('zoom', h); map.off('zoomend', h); };
  }, [map]);

  /**
   * ⚠️ THE DIAL IS A MULTIPLIER, NOT AN ABSOLUTE GROUND DISTANCE — the first attempt at fixing this
   * screen made it a true kilometre radius, and Evan's verdict was "isnt rly doing shit". He was
   * right, and the reason is arithmetic: a fixed ground radius spans a usable pixel size across only
   * about four zoom levels. At z3 (~15 km per pixel) every setting from 5 to 150 km lands under a
   * pixel and slams into the floor; at z13 (~15 m per pixel) every setting is thousands of pixels
   * and slams into the ceiling. The dial moved a number that the clamp then threw away, so it
   * appeared dead at exactly the zooms anyone actually looks at.
   *
   * So: pixel radius, scaled by a BOUNDED function of zoom. The multiplier keeps the fix that
   * mattered — blobs grow as you zoom in, so a city no longer shrivels to a dot — while the dial
   * multiplies the result and therefore always does something at every zoom. Growth is linear in
   * zoom rather than the true 2^zoom, because doubling per level is what forced the clamp that ate
   * the control in the first place.
   */
  const radiusPx = useMemo(() => {
    const Z_REF = 4;
    const growth = Math.max(0.45, Math.min(5, 1 + 0.32 * (zoom - Z_REF)));
    return Math.max(6, Math.min(300, spread * growth));
  }, [zoom, spread]);

  useEffect(() => {
    const layer = L.heatLayer(points, {
      radius: radiusPx,
      blur: radiusPx * 0.78,
      max,
      maxZoom: Math.max(1, Math.round(zoom)),
      minOpacity: 0.28 * brightness,
      gradient: HEAT_GRADIENT,
    });
    layer.addTo(map);
    /**
     * ⚠️ BRIGHTNESS HAS TO BE APPLIED TO THE CANVAS, NOT THE GRADIENT. simpleheat (under
     * leaflet.heat) accumulates intensity into an alpha channel and then reads only the RGB out of
     * the gradient palette, discarding whatever alpha the gradient stops carry — so dimming the
     * colours in HEAT_GRADIENT would change nothing at all. The layer's own canvas is the one place
     * a global ceiling can be set. `minOpacity` is scaled by the same factor so the faint end dims
     * with the peak rather than becoming proportionally louder as you turn brightness down.
     */
    const canvas = (layer as unknown as { _canvas?: HTMLCanvasElement })._canvas;
    if (canvas) canvas.style.opacity = String(brightness);
    return () => {
      map.removeLayer(layer);
    };
  }, [map, points, max, radiusPx, zoom, brightness]);
  return null;
}

function escapeHtml(raw: string): string {
  return raw.replace(/[&<>"']/g, (c) =>
    c === '&' ? '&amp;' : c === '<' ? '&lt;' : c === '>' ? '&gt;' : c === '"' ? '&quot;' : '&#39;',
  );
}

/** The value a city is judged on for the minimum. For a two-metric label, the larger of the two —
 *  "show this city if it clears the bar on something I am actually displaying". */
function labelValue(c: CityStat, metric: LabelMetric): number {
  if (metric === 'users') return c.users;
  if (metric === 'venues') return c.venues;
  if (metric === 'ratings') return c.ratings;
  return Math.max(c.users, c.venues);
}

/**
 * ⚠️ BUILT AS AN HTML STRING FOR `L.divIcon`, SO EVERY STYLE IS INLINE. Tailwind classes cannot be
 * used here: the markup never passes through the build's class scanner, so any utility not already
 * emitted elsewhere in the bundle silently renders unstyled — and this is the one part of the
 * screen destined for a screenshot, where "silently unstyled" is the whole cost.
 *
 * These are four genuinely different looks rather than four colour variants, because the job varies:
 * `pill` reads at a glance over busy tiles, `glass` matches the app's own chrome, `numeral` is
 * chromeless for a clean export, `stacked` is the only one that carries two metrics legibly.
 */
function labelHtml(c: CityStat, metric: LabelMetric, style: LabelStyle, scale: number): string {
  const city = escapeHtml(c.label);
  const n = (v: number) => v.toLocaleString();
  const px = (v: number) => `${(v * scale).toFixed(1)}px`;
  const shell =
    'position:absolute;left:0;top:0;transform:translate(-50%,-50%);white-space:nowrap;' +
    'font-family:Inter,system-ui,-apple-system,sans-serif;cursor:grab;';
  const primary =
    metric === 'users' ? n(c.users)
      : metric === 'venues' ? n(c.venues)
        : metric === 'ratings' ? n(c.ratings)
          : '';
  const unit =
    metric === 'users' ? (c.users === 1 ? 'user' : 'users')
      : metric === 'venues' ? (c.venues === 1 ? 'venue' : 'venues')
        : (c.ratings === 1 ? 'rating' : 'ratings');

  if (style === 'pill') {
    const body = metric === 'both'
      ? `${n(c.users)} · ${n(c.venues)}`
      : primary;
    return `<div style="${shell}display:flex;align-items:center;gap:${px(6)};background:#8B5CF6;` +
      `border:1px solid rgba(255,255,255,0.24);border-radius:999px;padding:${px(4)} ${px(10)};` +
      `box-shadow:0 ${px(4)} ${px(14)} rgba(0,0,0,0.45);">` +
      `<span style="color:#fff;font-size:${px(11)};font-weight:600;letter-spacing:-0.1px;">${city}</span>` +
      `<span style="color:#fff;font-size:${px(12.5)};font-weight:800;font-variant-numeric:tabular-nums;">${body}</span>` +
      `</div>`;
  }

  if (style === 'glass') {
    const body = metric === 'both'
      ? `<span style="color:#C4B5FD;font-size:${px(12.5)};font-weight:800;font-variant-numeric:tabular-nums;">${n(c.users)}</span>` +
        `<span style="color:rgba(255,255,255,0.3);font-size:${px(11)};">/</span>` +
        `<span style="color:#F5C842;font-size:${px(12.5)};font-weight:800;font-variant-numeric:tabular-nums;">${n(c.venues)}</span>`
      : `<span style="color:#C4B5FD;font-size:${px(12.5)};font-weight:800;font-variant-numeric:tabular-nums;">${primary}</span>`;
    return `<div style="${shell}display:flex;align-items:center;gap:${px(7)};` +
      `background:rgba(12,13,15,0.72);backdrop-filter:blur(10px);-webkit-backdrop-filter:blur(10px);` +
      `border:1px solid rgba(255,255,255,0.14);border-radius:${px(10)};padding:${px(4.5)} ${px(9)};` +
      `box-shadow:0 ${px(6)} ${px(18)} rgba(0,0,0,0.5);">` +
      `<span style="color:rgba(255,255,255,0.86);font-size:${px(11)};font-weight:600;">${city}</span>${body}</div>`;
  }

  if (style === 'numeral') {
    /** No chrome at all — the map is the design, the number is the message. */
    const shadow = `text-shadow:0 ${px(2)} ${px(10)} rgba(0,0,0,0.85);`;
    const body = metric === 'both' ? `${n(c.users)}<span style="opacity:0.5;"> · </span>${n(c.venues)}` : primary;
    return `<div style="${shell}text-align:center;">` +
      `<div style="color:#fff;font-size:${px(26)};font-weight:800;line-height:1;letter-spacing:-0.6px;` +
      `font-variant-numeric:tabular-nums;${shadow}">${body}</div>` +
      `<div style="color:rgba(255,255,255,0.72);font-size:${px(9.5)};font-weight:700;letter-spacing:${px(1.1)};` +
      `text-transform:uppercase;margin-top:${px(3)};${shadow}">${city}</div></div>`;
  }

  /** stacked — the only style that carries two numbers with their names attached. */
  const rows = metric === 'both'
    ? `<div style="display:flex;gap:${px(10)};margin-top:${px(4)};">` +
      `<div><div style="color:#C4B5FD;font-size:${px(15)};font-weight:800;line-height:1;font-variant-numeric:tabular-nums;">${n(c.users)}</div>` +
      `<div style="color:rgba(255,255,255,0.5);font-size:${px(8.5)};font-weight:700;letter-spacing:${px(0.8)};text-transform:uppercase;margin-top:${px(2)};">users</div></div>` +
      `<div><div style="color:#F5C842;font-size:${px(15)};font-weight:800;line-height:1;font-variant-numeric:tabular-nums;">${n(c.venues)}</div>` +
      `<div style="color:rgba(255,255,255,0.5);font-size:${px(8.5)};font-weight:700;letter-spacing:${px(0.8)};text-transform:uppercase;margin-top:${px(2)};">venues</div></div></div>`
    : `<div style="margin-top:${px(4)};"><div style="color:#C4B5FD;font-size:${px(17)};font-weight:800;line-height:1;font-variant-numeric:tabular-nums;">${primary}</div>` +
      `<div style="color:rgba(255,255,255,0.5);font-size:${px(8.5)};font-weight:700;letter-spacing:${px(0.8)};text-transform:uppercase;margin-top:${px(2)};">${unit}</div></div>`;
  return `<div style="${shell}background:rgba(12,13,15,0.82);border:1px solid rgba(255,255,255,0.12);` +
    `border-radius:${px(12)};padding:${px(7)} ${px(11)};box-shadow:0 ${px(8)} ${px(22)} rgba(0,0,0,0.55);">` +
    `<div style="color:#fff;font-size:${px(11)};font-weight:700;letter-spacing:-0.1px;">${city}</div>${rows}</div>`;
}

type LabelOffset = { x: number; y: number };
const LABEL_OFFSET_KEY = 'shado.admin.geo.labelOffsets.v1';

/**
 * ⚠️ THE OFFSET IS IN PIXELS AND LIVES ON `iconAnchor`, NOT IN THE MARKER'S POSITION. Dragging a
 * Leaflet marker normally rewrites its lat/lng, which would pin the label to a patch of GROUND —
 * so the moment you zoomed, a label nudged clear of its neighbour would slide back over it, because
 * a fixed ground distance is a changing screen distance. The whole point of moving a label is a
 * screen-space relationship.
 *
 * So the drag is measured in pixels, folded into the icon's anchor, and the marker is snapped back
 * to the city's true coordinates. The label sits exactly where it was dropped and keeps that
 * relationship at every zoom, while the marker still knows where the city actually is — which is
 * what lets the leader line stay honest once a label has been pulled away from its point.
 */
function CityLabelLayer({
  points, metric, style, scale, offsets, onMove,
}: {
  points: CityStat[];
  metric: LabelMetric;
  style: LabelStyle;
  scale: number;
  offsets: Record<string, LabelOffset>;
  onMove: (id: string, next: LabelOffset) => void;
}) {
  const map = useMap();
  const [, setTick] = useState(0);
  useEffect(() => {
    /** The leader line is drawn between two lat/lngs, one of which is derived from a pixel offset —
     *  so it has to be recomputed whenever the projection changes. */
    const h = () => setTick((t) => t + 1);
    map.on('zoomend', h);
    map.on('moveend', h);
    return () => { map.off('zoomend', h); map.off('moveend', h); };
  }, [map]);

  return (
    <>
      {points.map((c) => {
        const off = offsets[c.id] ?? { x: 0, y: 0 };
        const pulled = Math.abs(off.x) + Math.abs(off.y) > 16;
        let leaderEnd: L.LatLng | null = null;
        if (pulled) {
          try {
            leaderEnd = map.layerPointToLatLng(
              map.latLngToLayerPoint(L.latLng(c.lat, c.lng)).add(L.point(off.x, off.y)),
            );
          } catch { leaderEnd = null; }
        }
        return (
          <React.Fragment key={`label-${c.id}`}>
            {leaderEnd ? (
              <Polyline
                positions={[[c.lat, c.lng], [leaderEnd.lat, leaderEnd.lng]]}
                pathOptions={{ color: '#A78BFA', weight: 1, opacity: 0.4, dashArray: '3 4' }}
                interactive={false}
              />
            ) : null}
            <Marker
              position={[c.lat, c.lng]}
              draggable
              zIndexOffset={1000}
              eventHandlers={{
                dragend: (e) => {
                  const m = e.target as L.Marker;
                  const from = map.latLngToLayerPoint(L.latLng(c.lat, c.lng));
                  const to = map.latLngToLayerPoint(m.getLatLng());
                  onMove(c.id, { x: off.x + (to.x - from.x), y: off.y + (to.y - from.y) });
                  /** Back to the city; the anchor now carries where you dropped it. */
                  m.setLatLng(L.latLng(c.lat, c.lng));
                },
              }}
              icon={L.divIcon({
                html: labelHtml(c, metric, style, scale),
                className: '',
                iconSize: [0, 0],
                /** Negated: Leaflet places the anchor point ON the lat/lng, so a negative anchor
                 *  pushes the element the other way by the same pixels. */
                iconAnchor: [-off.x, -off.y],
              })}
            />
          </React.Fragment>
        );
      })}
    </>
  );
}

export function GeographyScreen() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [cities, setCities] = useState<CityCoordRow[]>([]);
  const [profiles, setProfiles] = useState<ProfileCityRow[]>([]);
  const [venues, setVenues] = useState<RatedVenueRow[]>([]);
  const [layer, setLayer] = useState<LayerKey>('users');
  const [display, setDisplay] = useState<DisplayMode>('circles');
  const [labelsOn, setLabelsOn] = useState(false);
  const [labelMetric, setLabelMetric] = useState<LabelMetric>('users');
  const [labelStyle, setLabelStyle] = useState<LabelStyle>('glass');
  const [labelSize, setLabelSize] = useState<LabelSize>('md');
  const [labelMin, setLabelMin] = useState(1);
  /** Kept across reloads: nudging 20 labels clear of each other for a screenshot is real work, and
   *  losing it to a refresh would make the feature not worth using. */
  const [labelOffsets, setLabelOffsets] = useState<Record<string, LabelOffset>>(() => {
    try {
      const raw = window.localStorage.getItem(LABEL_OFFSET_KEY);
      return raw ? (JSON.parse(raw) as Record<string, LabelOffset>) : {};
    } catch {
      return {};
    }
  });

  const moveLabel = useCallback((id: string, next: LabelOffset) => {
    setLabelOffsets((prev) => {
      const merged = { ...prev, [id]: next };
      try { window.localStorage.setItem(LABEL_OFFSET_KEY, JSON.stringify(merged)); } catch { /* private mode */ }
      return merged;
    });
  }, []);

  const resetLabelPositions = useCallback(() => {
    setLabelOffsets({});
    try { window.localStorage.removeItem(LABEL_OFFSET_KEY); } catch { /* private mode */ }
  }, []);
  /** Blob size multiplier. Exposed because the honest answer depends on the claim: tight blobs read
   *  as specific venues, broad ones as regional reach. */
  const [heatSpread, setHeatSpread] = useState(30);
  /** Peak opacity of the whole heat canvas. */
  const [heatBrightness, setHeatBrightness] = useState(1);
  /**
   * Multiplies the value that counts as full intensity. Below 1 the map runs hot and small places
   * still read; above 1 only genuine clusters reach the top of the ramp. The auto value is derived
   * from the data, so this is the knob for "that is not how hot this actually feels".
   */
  const [heatScale, setHeatScale] = useState(1);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [citiesRes, profileRows, venueRows] = await Promise.all([
        supabase.from('cities').select('id,name,lat,lng'),
        fetchAllRows<ProfileCityRow>((from, to) =>
          supabase.from('profiles').select('id,city').not('city', 'is', null).range(from, to),
        ),
        fetchAllRows<RatedVenueRow>((from, to) =>
          supabase
            .from('venues')
            .select('id,venue_name_user_input,latitude,longitude,venue_shado_rating_count')
            .gt('venue_shado_rating_count', 0)
            .range(from, to),
        ),
      ]);
      if (citiesRes.error) throw new Error(citiesRes.error.message);
      setCities(citiesRes.data ?? []);
      setProfiles(profileRows);
      setVenues(venueRows);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to load geography data');
      setCities([]);
      setProfiles([]);
      setVenues([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadData();
  }, [loadData]);

  // Index cities by the lowercased first comma-segment of their canonical name.
  const cityIndex = useMemo(() => {
    const m = new Map<string, CityCoordRow>();
    for (const c of cities) {
      if (!c.name || c.lat == null || c.lng == null) continue;
      const key = cityKey(c.name);
      if (!m.has(key)) m.set(key, c);
    }
    return m;
  }, [cities]);

  const { cityUserPoints, unmatchedCities, matchedUserTotal, unmatchedUserTotal } = useMemo(() => {
    const byCityId = new Map<string, number>();
    const unmatched = new Map<string, { label: string; count: number }>();
    for (const p of profiles) {
      const raw = (p.city ?? '').trim();
      if (!raw) continue;
      const key = cityKey(raw);
      const city = cityIndex.get(key);
      if (city) {
        byCityId.set(city.id, (byCityId.get(city.id) ?? 0) + 1);
      } else {
        const label = firstSegment(raw);
        const existing = unmatched.get(key);
        unmatched.set(key, { label: existing?.label ?? label, count: (existing?.count ?? 0) + 1 });
      }
    }
    const points: CityUserPoint[] = [];
    let matchedTotal = 0;
    const cityById = new Map(cities.map((c) => [c.id, c]));
    byCityId.forEach((count, cityId) => {
      const c = cityById.get(cityId);
      if (!c || !c.name || c.lat == null || c.lng == null) return;
      points.push({ id: cityId, label: firstSegment(c.name), lat: c.lat, lng: c.lng, count });
      matchedTotal += count;
    });
    points.sort((a, b) => b.count - a.count);
    const unmatchedList: UnmatchedCityAgg[] = Array.from(unmatched.entries())
      .map(([key, v]) => ({ key, label: v.label, count: v.count }))
      .sort((a, b) => b.count - a.count);
    const unmatchedTotal = unmatchedList.reduce((sum, u) => sum + u.count, 0);
    return {
      cityUserPoints: points,
      unmatchedCities: unmatchedList,
      matchedUserTotal: matchedTotal,
      unmatchedUserTotal: unmatchedTotal,
    };
  }, [profiles, cityIndex, cities]);

  const { venuePoints, venuesMissingCoords } = useMemo(() => {
    const pts: VenuePoint[] = [];
    let missing = 0;
    for (const v of venues) {
      if (v.latitude == null || v.longitude == null) {
        missing += 1;
        continue;
      }
      pts.push({
        id: v.id,
        label: v.venue_name_user_input?.trim() || 'Unnamed venue',
        lat: v.latitude,
        lng: v.longitude,
        count: v.venue_shado_rating_count ?? 0,
      });
    }
    pts.sort((a, b) => b.count - a.count);
    return { venuePoints: pts, venuesMissingCoords: missing };
  }, [venues]);

  /**
   * Per-city totals for the label layer: home-city users, plus every rated venue credited to its
   * nearest featured city.
   *
   * ⚠️ BUILT FROM ALL 267 CITIES, NOT FROM THE USER POINTS. A city with venues rated in it but no
   * user who calls it home is exactly the kind of place worth showing on a coverage map, and
   * starting from the user list would have silently dropped every one of them.
   */
  const cityStats = useMemo<CityStat[]>(() => {
    const byId = new Map<string, CityStat>();
    for (const c of cities) {
      if (!c.name || c.lat == null || c.lng == null) continue;
      byId.set(c.id, { id: c.id, label: firstSegment(c.name), lat: c.lat, lng: c.lng, users: 0, venues: 0, ratings: 0 });
    }
    for (const p of cityUserPoints) {
      const e = byId.get(p.id);
      if (e) e.users = p.count;
    }
    const list = Array.from(byId.values());
    for (const v of venuePoints) {
      let best: CityStat | null = null;
      let bestD = VENUE_CITY_RADIUS_KM;
      for (const c of list) {
        /** Degree-box reject before the trig — 267 cities × every rated venue otherwise. */
        if (Math.abs(c.lat - v.lat) > 0.9 || Math.abs(c.lng - v.lng) > 1.7) continue;
        const d = haversineKm(c.lat, c.lng, v.lat, v.lng);
        if (d < bestD) { bestD = d; best = c; }
      }
      if (best) { best.venues += 1; best.ratings += v.count; }
    }
    return list;
  }, [cities, cityUserPoints, venuePoints]);

  const labelPoints = useMemo(() => {
    const min = Number.isFinite(labelMin) ? labelMin : 1;
    return cityStats
      .filter((c) => labelValue(c, labelMetric) >= Math.max(1, min))
      .sort((a, b) => labelValue(b, labelMetric) - labelValue(a, labelMetric))
      /** Hard cap: past ~50 the labels overlap into noise and the screenshot is unusable. The
       *  threshold is the real control; this only stops a min of 1 from carpeting the map. */
      .slice(0, 50);
  }, [cityStats, labelMetric, labelMin]);

  const labelTotals = useMemo(() => {
    return labelPoints.reduce(
      (acc, c) => ({ users: acc.users + c.users, venues: acc.venues + c.venues, ratings: acc.ratings + c.ratings }),
      { users: 0, venues: 0, ratings: 0 },
    );
  }, [labelPoints]);

  /** Bumped by the Fit button so an explicit re-frame overrides the once-only rule. */
  const [fitNonce, setFitNonce] = useState(0);

  const activePoints = useMemo<[number, number][]>(() => {
    if (layer === 'users') return cityUserPoints.map((p): [number, number] => [p.lat, p.lng]);
    return venuePoints.map((v): [number, number] => [v.lat, v.lng]);
  }, [layer, cityUserPoints, venuePoints]);

  /**
   * Weighted [lat, lng, weight] tuples for heat mode, plus the `max` the weights are
   * relative to. Users/density weight by count with max = the largest count, so the
   * top city/venue hits the gold peak and everything else scales proportionally.
   * Venues weight 1-per-venue with max = 3, so isolated venues render as cool violet
   * and only real clusters (3+ overlapping) climb toward the peak — pure density.
   */
  const heat = useMemo<{ points: L.HeatLatLngTuple[]; max: number }>(() => {
    // sqrt-compress weights (2026-07-04, Evan): a single user/venue lands solidly purple
    // instead of near-invisible next to the top city; clusters still climb to gold.
    /** `heatScale` multiplies the ceiling: <1 makes everything hotter, >1 reserves the peak. */
    const k = Math.max(0.15, heatScale);
    if (layer === 'users') {
      const top = cityUserPoints.reduce((m, p) => Math.max(m, p.count), 0) || 1;
      return {
        points: cityUserPoints.map((p): L.HeatLatLngTuple => [p.lat, p.lng, Math.sqrt(p.count)]),
        max: Math.sqrt(top) * 0.8 * k,
      };
    }
    if (layer === 'venues') {
      return { points: venuePoints.map((v): L.HeatLatLngTuple => [v.lat, v.lng, 1]), max: 2 * k };
    }
    const top = venuePoints.reduce((m, v) => Math.max(m, v.count), 0) || 1;
    return {
      points: venuePoints.map((v): L.HeatLatLngTuple => [v.lat, v.lng, Math.sqrt(v.count)]),
      max: Math.sqrt(top) * 0.8 * k,
    };
  }, [layer, cityUserPoints, venuePoints, heatScale]);

  const summary = useMemo((): {
    plottedLabel: string;
    plotted: number;
    unplottedLabel: string;
    unplotted: number;
    unplottedSub: string | null;
    topTitle: string;
    top: TopRow[];
  } => {
    if (layer === 'users') {
      return {
        plottedLabel: 'Users plotted',
        plotted: matchedUserTotal,
        unplottedLabel: 'Users unplotted',
        unplotted: unmatchedUserTotal,
        unplottedSub:
          unmatchedCities.length > 0 ? `${unmatchedCities.length.toLocaleString()} distinct city strings — table below` : 'All home cities matched',
        topTitle: 'Top cities',
        top: cityUserPoints.slice(0, 5).map((p) => ({ label: p.label, count: p.count })),
      };
    }
    const layerNoun = layer === 'venues' ? 'ratings' : 'rating density';
    return {
      plottedLabel: 'Venues plotted',
      plotted: venuePoints.length,
      unplottedLabel: 'Venues unplotted',
      unplotted: venuesMissingCoords,
      unplottedSub: venuesMissingCoords > 0 ? 'Rated venues missing lat/lng' : 'All rated venues have coordinates',
      topTitle: layer === 'venues' ? 'Top rated venues' : `Top by ${layerNoun}`,
      top: venuePoints.slice(0, 5).map((v) => ({ label: v.label, count: v.count })),
    };
  }, [layer, matchedUserTotal, unmatchedUserTotal, unmatchedCities.length, cityUserPoints, venuePoints, venuesMissingCoords]);

  return (
    <div className="admin-page max-w-6xl mx-auto">
      <PageHeader
        title="Geography"
        subtitle="Where users and rated venues sit on the map — home-city clusters, individual rated venues, and rating density."
        actions={
          <button type="button" onClick={() => void loadData()} disabled={loading} className="admin-btn admin-btn-secondary admin-btn-sm">
            {loading ? 'Loading…' : 'Refresh'}
          </button>
        }
      />

      {error ? <ErrorBanner className="mb-6">{error}</ErrorBanner> : null}

      {loading ? (
        <>
          <div className="flex items-center gap-2 mb-6">
            <Skeleton className="h-7 w-16" />
            <Skeleton className="h-7 w-28" />
            <Skeleton className="h-7 w-32" />
          </div>
          <SkeletonCards cards={3} className="mb-6" />
          <Skeleton className="h-[420px] sm:h-[520px] lg:h-[620px] w-full" />
        </>
      ) : (
        <>
          <div className="flex flex-wrap items-center gap-2 mb-6">
            <div className="flex items-center gap-2" role="tablist" aria-label="Geography layer">
              {LAYER_OPTIONS.map((opt) => (
                <button
                  key={opt.key}
                  type="button"
                  role="tab"
                  aria-selected={layer === opt.key}
                  data-active={layer === opt.key}
                  onClick={() => setLayer(opt.key)}
                  className="admin-pill"
                >
                  {opt.label}
                </button>
              ))}
            </div>
            <div className="ml-auto flex items-center gap-2" role="radiogroup" aria-label="Display mode">
              <span className="admin-eyebrow mr-1">Display</span>
              {DISPLAY_OPTIONS.map((opt) => (
                <button
                  key={opt.key}
                  type="button"
                  role="radio"
                  aria-checked={display === opt.key}
                  data-active={display === opt.key}
                  onClick={() => setDisplay(opt.key)}
                  className="admin-pill"
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          {display === 'heat' ? (
            <div className="mb-6 flex flex-wrap items-center gap-3 rounded-lg border border-border bg-surface px-3 py-2.5">
              <span className="admin-eyebrow">Blob size</span>
              <input
                type="range"
                min={6}
                max={90}
                step={1}
                value={heatSpread}
                onChange={(e) => setHeatSpread(Number(e.target.value))}
                className="h-1 flex-1 min-w-[160px] max-w-[320px] cursor-pointer accent-[#8B5CF6]"
                aria-label="Heat blob size"
              />
              <span className="tabular-nums text-[12px] text-muted">{heatSpread}</span>

              <span className="admin-eyebrow ml-2">Brightness</span>
              <input
                type="range"
                min={20}
                max={100}
                step={5}
                value={Math.round(heatBrightness * 100)}
                onChange={(e) => setHeatBrightness(Number(e.target.value) / 100)}
                className="h-1 w-[130px] cursor-pointer accent-[#8B5CF6]"
                aria-label="Heat brightness"
              />
              <span className="tabular-nums text-[12px] text-muted">{Math.round(heatBrightness * 100)}%</span>

              <span className="admin-eyebrow ml-2">Scale</span>
              <input
                type="range"
                min={20}
                max={300}
                step={5}
                value={Math.round(heatScale * 100)}
                onChange={(e) => setHeatScale(Number(e.target.value) / 100)}
                className="h-1 w-[130px] cursor-pointer accent-[#8B5CF6]"
                aria-label="Heat intensity scale"
              />
              <span className="tabular-nums text-[12px] text-muted">{heatScale.toFixed(2)}×</span>

              <button
                type="button"
                onClick={() => { setHeatSpread(30); setHeatBrightness(1); setHeatScale(1); }}
                className="admin-pill"
              >
                Reset
              </button>
              <span className="w-full text-[11px] text-mutedDim">
                Blobs grow as you zoom in, so a city keeps its weight instead of shrivelling.
                Scale below 1× runs the map hot so small places still read; above 1× reserves the top
                of the ramp for genuine clusters.
              </span>
            </div>
          ) : null}

          <div className="mb-6 rounded-lg border border-border bg-surface px-3 py-2.5">
            <div className="flex flex-wrap items-center gap-2">
              <button
                type="button"
                data-active={labelsOn}
                onClick={() => setLabelsOn((v) => !v)}
                className="admin-pill"
              >
                {labelsOn ? 'City stats on' : 'Show city stats'}
              </button>
              {labelsOn ? (
                <>
                  <span className="admin-eyebrow ml-1">Show</span>
                  {LABEL_METRIC_OPTIONS.map((opt) => (
                    <button
                      key={opt.key}
                      type="button"
                      data-active={labelMetric === opt.key}
                      onClick={() => setLabelMetric(opt.key)}
                      className="admin-pill"
                    >
                      {opt.label}
                    </button>
                  ))}
                  <label className="ml-1 flex items-center gap-1.5 text-[11px] text-mutedDim">
                    <span className="admin-eyebrow">Min</span>
                    <input
                      type="number"
                      min={1}
                      value={labelMin}
                      onChange={(e) => setLabelMin(Math.max(1, Number(e.target.value) || 1))}
                      className="w-16 rounded border border-border bg-bg px-2 py-1 text-[12px] tabular-nums text-textPrimary"
                      aria-label="Minimum per city"
                    />
                  </label>
                </>
              ) : null}
            </div>
            {labelsOn ? (
              <div className="mt-2.5 flex flex-wrap items-center gap-2 border-t border-border/60 pt-2.5">
                <span className="admin-eyebrow">Style</span>
                {LABEL_STYLE_OPTIONS.map((opt) => (
                  <button
                    key={opt.key}
                    type="button"
                    data-active={labelStyle === opt.key}
                    onClick={() => setLabelStyle(opt.key)}
                    className="admin-pill"
                  >
                    {opt.label}
                  </button>
                ))}
                <span className="admin-eyebrow ml-2">Size</span>
                {(['sm', 'md', 'lg'] as LabelSize[]).map((k) => (
                  <button
                    key={k}
                    type="button"
                    data-active={labelSize === k}
                    onClick={() => setLabelSize(k)}
                    className="admin-pill"
                  >
                    {k.toUpperCase()}
                  </button>
                ))}
                {Object.keys(labelOffsets).length > 0 ? (
                  <button type="button" onClick={resetLabelPositions} className="admin-pill">
                    Reset positions
                  </button>
                ) : null}
                <span className="ml-auto text-[11px] text-mutedDim tabular-nums">
                  drag a label to move it · {labelPoints.length.toLocaleString()} cities ·{' '}
                  {labelTotals.users.toLocaleString()} users · {labelTotals.venues.toLocaleString()} venues ·{' '}
                  {labelTotals.ratings.toLocaleString()} ratings
                </span>
              </div>
            ) : null}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-[minmax(160px,200px)_minmax(160px,200px)_minmax(240px,1fr)] gap-4 mb-6">
            <StatCard label={summary.plottedLabel} value={summary.plotted} />
            <StatCard label={summary.unplottedLabel} value={summary.unplotted} sub={summary.unplottedSub} />
            <div className="admin-card min-w-0 sm:col-span-2 lg:col-span-1">
              <div className="admin-stat-card__label mb-3">{summary.topTitle}</div>
              {summary.top.length === 0 ? (
                <p className="text-sm text-mutedDim">No data yet.</p>
              ) : (
                <ol className="space-y-1.5">
                  {summary.top.map((row, i) => (
                    <li key={`${row.label}-${i}`} className="flex items-center justify-between gap-3 text-[13px]">
                      <span className="flex items-center gap-2 min-w-0">
                        <span className="text-mutedDim tabular-nums w-4 shrink-0">{i + 1}</span>
                        <span className="truncate text-textPrimary">{row.label}</span>
                      </span>
                      <span className="shrink-0 tabular-nums text-muted">{row.count.toLocaleString()}</span>
                    </li>
                  ))}
                </ol>
              )}
            </div>
          </div>

          <div className="admin-map relative w-full overflow-hidden rounded-lg border border-border bg-surface h-[420px] sm:h-[520px] lg:h-[620px]">
            <MapContainer
              center={[20, 0]}
              zoom={2}
              minZoom={2}
              maxZoom={18}
              worldCopyJump
              /* Continuous zoom: no snapping to integer levels, small button/dblclick step,
                 and a slower wheel rate (120px per level, default 60) with a short debounce
                 so trackpad/scroll zoom glides instead of jumping. zoomAnimation stays on
                 (Leaflet default). fitBounds lands on exact fractional zoom with snap 0. */
              zoomSnap={0}
              zoomDelta={0.25}
              wheelPxPerZoomLevel={120}
              wheelDebounceTime={25}
              style={{ height: '100%', width: '100%', background: '#0f1011' }}
            >
              <TileLayer
                url="https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png"
                subdomains="abcd"
                maxZoom={19}
                detectRetina
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions">CARTO</a>'
              />
              <FitToPoints points={activePoints} fitNonce={fitNonce} />
              <MapHud onFit={() => setFitNonce((n) => n + 1)} />

              {display === 'heat' && <HeatmapLayer points={heat.points} max={heat.max} spread={heatSpread} brightness={heatBrightness} />}

              {display === 'circles' &&
                layer === 'users' &&
                cityUserPoints.map((p) => (
                  <CircleMarker
                    key={p.id}
                    center={[p.lat, p.lng]}
                    radius={userMarkerRadius(p.count)}
                    pathOptions={{ color: '#B79CFF', weight: 1.5, opacity: 0.9, fillColor: '#8B5CF6', fillOpacity: 0.5 }}
                  >
                    <LeafletTooltip direction="top" offset={[0, -4]}>
                      {p.label} — {pluralize(p.count, 'user')}
                    </LeafletTooltip>
                  </CircleMarker>
                ))}

              {display === 'circles' &&
                layer === 'venues' &&
                venuePoints.map((v) => (
                  <CircleMarker
                    key={v.id}
                    center={[v.lat, v.lng]}
                    radius={VENUE_DOT_RADIUS}
                    pathOptions={{ color: '#B79CFF', weight: 1, opacity: 0.9, fillColor: '#8B5CF6', fillOpacity: 0.75 }}
                  >
                    <LeafletTooltip direction="top" offset={[0, -4]}>
                      {v.label} — {pluralize(v.count, 'rating')}
                    </LeafletTooltip>
                  </CircleMarker>
                ))}

              {display === 'circles' &&
                layer === 'density' &&
                venuePoints.map((v) => (
                  <CircleMarker
                    key={v.id}
                    center={[v.lat, v.lng]}
                    radius={densityMarkerRadius(v.count)}
                    pathOptions={{ stroke: false, fillColor: '#8B5CF6', fillOpacity: 0.22 }}
                  >
                    <LeafletTooltip direction="top" offset={[0, -4]}>
                      {v.label} — {pluralize(v.count, 'rating')}
                    </LeafletTooltip>
                  </CircleMarker>
                ))}
              {/* Labels last so they sit above every marker and the heat canvas. */}
              {labelsOn ? (
                <CityLabelLayer
                  points={labelPoints}
                  metric={labelMetric}
                  style={labelStyle}
                  scale={LABEL_SIZE_SCALE[labelSize]}
                  offsets={labelOffsets}
                  onMove={moveLabel}
                />
              ) : null}
            </MapContainer>
          </div>

          {layer === 'users' && unmatchedCities.length > 0 ? (
            <div className="mt-6">
              <p className="admin-eyebrow mb-3">No coordinates (not a featured city)</p>
              <div className="rounded-xl border border-border bg-surface overflow-x-auto">
                <table className="w-full border-collapse text-sm">
                  <thead>
                    <tr className="admin-table-head">
                      <th scope="col" className="sticky top-0 z-10 border-b border-border/80 bg-surface px-4 py-3">
                        City (as entered)
                      </th>
                      <th scope="col" className="sticky top-0 z-10 border-b border-border/80 bg-surface px-4 py-3 text-right">
                        Users
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {unmatchedCities.map((u, i) => (
                      <tr key={u.key} className={`border-b border-border/50 ${i % 2 === 0 ? 'bg-surface' : 'bg-rowAlt'}`}>
                        <td className="px-4 py-2.5 text-textPrimary">{u.label}</td>
                        <td className="px-4 py-2.5 text-right tabular-nums text-muted">{u.count.toLocaleString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ) : null}
        </>
      )}
    </div>
  );
}
